import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaApple,
  FaBoxes,
  FaTimes,
  FaTruck,
  FaWarehouse,
} from "react-icons/fa";
import { SiHuawei } from "react-icons/si";
import appIcon from "./Assets/doorstop-app-icon copy.png";
import heroArt from "./Assets/doorstop-app-icon-cyan-detailed.png";
import playIcon from "./Assets/playIcon.png";
import "./Home.css";

const PLAY_STORE_WEB =
  "https://play.google.com/store/apps/details?id=com.instantdigits.doorStop&pcampaignid=web_share";
const PLAY_STORE_INTENT =
  "intent://details?id=com.instantdigits.doorStop#Intent;scheme=market;action=android.intent.action.VIEW;package=com.android.vending;end";
const APP_STORE = "https://apps.apple.com/ca/app/doorstoplk/id6775699768";
const APP_GALLERY_WEB = PLAY_STORE_WEB;
const APP_GALLERY_INTENT = PLAY_STORE_INTENT;

const features = [
  {
    icon: FaBoxes,
    title: "Catalog and quick reorder",
  },
  {
    icon: FaTruck,
    title: "Live orders and site delivery",
  },
  {
    icon: FaWarehouse,
    title: "Site-wise stock, not just a shopping cart",
  },
];

const detectPlatform = () => {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  const isIPadOS =
    navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  const isIOS = /iPhone|iPad|iPod/i.test(ua) || isIPadOS;
  const isHuawei = /Huawei|HUAWEI|HMSCore|HarmonyOS/i.test(ua);
  const isAndroid = /Android/i.test(ua);

  if (isIOS) return "ios";
  if (isHuawei) return "huawei";
  if (isAndroid) return "android";
  return null;
};

const storeForPlatform = (platform) => {
  if (platform === "ios") {
    return {
      kind: "apple",
      action: "Continue on the App Store",
      url: APP_STORE,
    };
  }
  if (platform === "android") {
    return {
      kind: "play",
      action: "Continue on Google Play",
      url: PLAY_STORE_INTENT,
    };
  }
  if (platform === "huawei") {
    return {
      kind: "gallery",
      action: "Continue on AppGallery",
      url: APP_GALLERY_INTENT,
    };
  }
  return null;
};

const STORE_CHIP_ORDER = {
  ios: ["apple", "play", "gallery"],
  android: ["play", "apple", "gallery"],
  huawei: ["gallery", "play", "apple"],
  desktop: ["play", "apple", "gallery"],
};

const storeChipHref = (id, platform) => {
  if (id === "apple") return APP_STORE;
  if (id === "play") {
    return platform === "android" || platform === "huawei"
      ? PLAY_STORE_INTENT
      : PLAY_STORE_WEB;
  }
  return platform === "android" || platform === "huawei"
    ? APP_GALLERY_INTENT
    : APP_GALLERY_WEB;
};

const overlayTitle = (kind) => {
  if (kind === "apple") return "Get DoorStop on Apple";
  if (kind === "gallery") return "Get DoorStop on Huawei";
  return "Get DoorStop on Android";
};

const overlayViewLabel = (kind) => {
  if (kind === "apple") return "View on App Store";
  if (kind === "gallery") return "View on AppGallery";
  return "View on Play Store";
};

const overlayStoreIcon = (kind) => {
  if (kind === "apple")
    return <FaApple className="ds-store-mark apple" title="App Store" />;
  if (kind === "gallery")
    return <SiHuawei className="ds-store-mark gallery" title="AppGallery" />;
  return <img src={playIcon} alt="Google Play" className="ds-store-mark play-img" />;
};

const Home = () => {
  const navigate = useNavigate();
  const [platform, setPlatform] = useState(null);
  const [store, setStore] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [qrStore, setQrStore] = useState(null);
  const storeTimer = useRef(null);

  useEffect(() => {
    const detected = detectPlatform();
    setPlatform(detected);

    const target = storeForPlatform(detected);
    if (!target) return;

    setStore(target);
    setShowInstall(true);

    if (detected === "ios") {
      storeTimer.current = setTimeout(() => {
        if (document.visibilityState === "hidden") return;
        window.location.href = target.url;
      }, 400);
    }

    return () => {
      if (storeTimer.current) clearTimeout(storeTimer.current);
    };
  }, []);

  const stayOnWeb = () => {
    if (storeTimer.current) clearTimeout(storeTimer.current);
    setShowInstall(false);
  };

  const isPhone =
    platform === "ios" || platform === "android" || platform === "huawei";
  const usesStoreTap = platform === "android" || platform === "huawei";
  const chipOrder = STORE_CHIP_ORDER[platform] || STORE_CHIP_ORDER.desktop;

  const nativeStoreKind =
    platform === "ios"
      ? "apple"
      : platform === "android"
        ? "play"
        : platform === "huawei"
          ? "gallery"
          : null;

  const openStoreQr = (event, nextQr) => {
    if (isPhone && nextQr.kind === nativeStoreKind) return;
    event.preventDefault();
    setShowInstall(false);
    setQrStore(nextQr);
  };

  const overlayIcons = (kind, iconSrc = appIcon) => (
    <div className="ds-overlay-brands">
      <img src={iconSrc} alt="DoorStop" className="ds-install-icon" />
      <span className="ds-overlay-plus" aria-hidden="true">
        +
      </span>
      {overlayStoreIcon(kind)}
    </div>
  );

  return (
    <div className="ds-page">
      {qrStore && (
        <div className="ds-install-overlay" role="dialog" aria-modal="true">
          <div className="ds-install-card ds-qr-card">
            <button
              className="ds-install-close"
              type="button"
              onClick={() => setQrStore(null)}
              aria-label="Close"
            >
              <FaTimes />
            </button>
            {overlayIcons(qrStore.kind, heroArt)}
            <h2>{overlayTitle(qrStore.kind)}</h2>
            <div className="ds-qr-box">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&data=${encodeURIComponent(
                  qrStore.url,
                )}`}
                alt={`QR code for ${qrStore.title}`}
                width={220}
                height={220}
              />
            </div>
            <div className="ds-qr-footer">
              <p className="ds-qr-hint">Scan me with your camera</p>
              <a
                className="ds-qr-view"
                href={qrStore.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {overlayViewLabel(qrStore.kind)}
              </a>
            </div>
          </div>
        </div>
      )}

      {showInstall && store && (
        <div
          className={`ds-install-overlay${
            usesStoreTap ? " is-android" : ""
          }`}
          role="dialog"
          aria-modal="true"
        >
          {usesStoreTap && (
            <a
              className="ds-install-hit"
              href={store.url}
              aria-label={store.action}
            />
          )}
          <div className="ds-install-card">
            <button
              className="ds-install-close"
              type="button"
              onClick={stayOnWeb}
              aria-label="Close"
            >
              <FaTimes />
            </button>
            {overlayIcons(store.kind)}
            <h2>{overlayTitle(store.kind)}</h2>
            {usesStoreTap ? (
              <span className="ds-install-open">{store.action}</span>
            ) : (
              <a className="ds-install-open" href={store.url}>
                {store.action}
              </a>
            )}
          </div>
        </div>
      )}

      <header className="ds-topbar">
        <div className="ds-brand">
          <img src={appIcon} alt="DoorStop" className="ds-brand-icon" />
          <span>DoorStop</span>
        </div>
        <button
          className="ds-home-btn"
          type="button"
          onClick={() => navigate("/")}
        >
          Instant Digits Home
        </button>
      </header>

      <section className="ds-hero">
        <div className="ds-hero-copy">
          <p className="ds-kicker">Building supplies, delivered</p>
          <h1>DoorStop — materials to your door</h1>
          <p className="ds-subhead">
            Order hardware and building supplies for the site, from your phone.
          </p>
          <p className="ds-one-line">
            DoorStop is the app to browse the catalog, send an order, and track
            delivery to your job site.
          </p>
          <div className={`ds-stores${isPhone ? " is-device" : ""}`}>
            {chipOrder.map((id, index) => {
              const chip =
                id === "apple"
                  ? {
                      kind: "apple",
                      title: "App Store",
                      url: APP_STORE,
                      small: "Download on the",
                      strong: "App Store",
                      icon: <FaApple className="ds-store-chip-icon" />,
                    }
                  : id === "play"
                    ? {
                        kind: "play",
                        title: "Google Play",
                        url: PLAY_STORE_WEB,
                        small: "GET IT ON",
                        strong: "Google Play",
                        icon: (
                          <img
                            src={playIcon}
                            alt=""
                            className="ds-store-chip-icon-img"
                          />
                        ),
                      }
                    : {
                        kind: "gallery",
                        title: "AppGallery",
                        url: APP_GALLERY_WEB,
                        small: "EXPLORE IT ON",
                        strong: "AppGallery",
                        icon: <SiHuawei className="ds-store-chip-icon" />,
                      };
              const isPrimary = isPhone && index === 0;

              return (
                <a
                  key={id}
                  className={`ds-store-chip ${id}${
                    isPhone ? (isPrimary ? " is-primary" : " is-secondary") : ""
                  }`}
                  href={storeChipHref(id, platform)}
                  target={
                    platform === "ios" && id === "apple" ? "_blank" : undefined
                  }
                  rel={
                    platform === "ios" && id === "apple"
                      ? "noopener noreferrer"
                      : undefined
                  }
                  onClick={(event) =>
                    openStoreQr(event, {
                      kind: chip.kind,
                      title: chip.title,
                      url: chip.url,
                    })
                  }
                >
                  {chip.icon}
                  <span className="ds-store-chip-text">
                    <small>{chip.small}</small>
                    <strong>{chip.strong}</strong>
                  </span>
                </a>
              );
            })}
          </div>
        </div>
        <div className="ds-hero-art">
          <img src={heroArt} alt="DoorStop delivery to the job site" />
        </div>
      </section>

      <section className="ds-body">
        <p className="ds-paragraph">
          DoorStop brings building materials to the site. Browse by category or
          need, save a cart, place the order, and follow it until it is
          delivered. Keep each work site’s stock in one place — in English,
          Tamil, or Sinhala.
        </p>

        <ul className="ds-features">
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <li key={feature.title} className="ds-feature">
                <span className="ds-feature-icon">
                  <Icon />
                </span>
                <span>{feature.title}</span>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
};

export default Home;
